import cx from "classnames";
import type { ButtonHTMLAttributes } from "react";

import HeartFilledIcon from "@/components/Icons/heart_filled.svg";

type Props = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "aria-label" | "aria-pressed"
> & {
  /**
   * Determines the filled-in state of the button
   */
  isFavorited: boolean;
  /**
   * The subject to be favoriting (e.g., dispensary name, strain name, etc.)
   */
  subject: string;
  /**
   * When numeral theme is selected, the count to display
   */
  count?: number | string;
} & (
    | {
        theme?: "analog";
      }
    | { theme: "numeral"; count: number | string }
  );

const FavoriteButton = ({
  className,
  isFavorited,
  subject,
  theme,
  count,
  ...props
}: Props) => {
  const ariaLabel = isFavorited
    ? `Unfavorite ${subject}`
    : `Favorite ${subject}`;

  const label = (() => {
    switch (theme) {
      case "analog":
        return isFavorited ? "Favorited" : "Favorite";
      case "numeral":
        return count.toLocaleString();
      default:
        return null;
    }
  })();

  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={isFavorited}
      className={cx(
        "p-1.5 bg-white border border-light-grey flex items-center rounded-full min-w-fit",
        { "py-1.5 px-md": !!label },
        className,
      )}
      title={ariaLabel}
      {...props}
    >
      <HeartFilledIcon
        className={cx(
          "stroke-green transition-text stroke-[1.65] duration-200",
          {
            "text-green": isFavorited,
            "text-white": !isFavorited,
          },
        )}
        height="22"
        width="22"
      />
      {!!label && <span className="text-xs font-bold ml-xs">{label}</span>}
    </button>
  );
};
export default FavoriteButton;
